// Owner mailbox provisioning (#236) — per-business ingest/send identities over
// /api/owner/tenants/:id/mailboxes. Credentials are write-only: the API returns only
// `*PasswordSet` booleans, the UI shows a masked placeholder, and an untouched password
// field on edit means "keep". The connection check is strictly read-only on the server.
import React from "react";
import { Banner, Button, FormField, Input, StatusPill, Switch } from "@/ds";
import { Icon } from "@/lib";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type { TenantMailboxCheckResult, TenantMailboxRow } from "@/api";
import { useT } from "../i18n";
import {
  NeoMailboxPicker,
  pickedAddress,
  useNeoMailboxOffer,
  type PickerState,
} from "./neo-mailbox-picker";
import {
  buildCreatePayload,
  buildUpdatePayload,
  emptyMailboxForm,
  mailboxFormErrors,
  mailboxFormFromRow,
  type MailboxForm,
} from "./screen-tenant-mailboxes.logic";
import type { PushToast } from "@/notifications/toast-queue";

export interface TenantMailboxesEditorProps {
  /** #378: rendered inline as a business-detail section (was a dialog). */
  tenantId: string;
  pushToast: PushToast;
  /**
   * #1432: the mailboxes after every read that succeeded, so Settings → Email can ask the server
   * again which addresses receive and where their replies leave from.
   */
  onRowsChange?: (rows: TenantMailboxRow[]) => void;
}

const MASK = "••••••••";

// #710: "create" now covers two paths. `neo` provisions on the platform's mail server; `create` is the
// unchanged bring-your-own form. Keeping them as modes rather than a flag means the two forms
// never share a half-filled state.
type FormMode =
  | { kind: "create" }
  | { kind: "neo" }
  // #779: correcting an address a business already has. Its own mode rather than a flag on
  // `neo`, because it closes a live address and has to say so before it happens.
  | { kind: "change"; row: TenantMailboxRow }
  // #1447: the same correction for the address a business's own domain receives at. Separate from
  // `change` because that one picks a name on OUR domain, checked against our availability.
  | { kind: "domainChange"; row: TenantMailboxRow }
  | { kind: "edit"; row: TenantMailboxRow };

/** #1536: the rows this business showed last time, while they are still remembered. */
function rememberedMailboxes(tenantId: string): TenantMailboxRow[] | null {
  if (!tenantId) return null;
  const list = cachedRead("listTenantMailboxes", tenantId);
  return Array.isArray(list) ? list : null;
}

export function TenantMailboxesEditor({ tenantId, pushToast, onRowsChange }: TenantMailboxesEditorProps) {
  const t = useT();
  const [rows, setRows] = React.useState<TenantMailboxRow[] | null>(() => rememberedMailboxes(tenantId));
  // Read through a ref so a parent passing a fresh callback each render cannot re-run this.
  const onRowsChangeRef = React.useRef(onRowsChange);
  onRowsChangeRef.current = onRowsChange;
  React.useEffect(() => {
    if (rows) onRowsChangeRef.current?.(rows);
  }, [rows]);
  const [error, setError] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<FormMode | null>(null);
  const [form, setForm] = React.useState<MailboxForm>(emptyMailboxForm());
  const [busy, setBusy] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [check, setCheck] = React.useState<{ email: string; result: TenantMailboxCheckResult } | null>(null);
  const [checkingId, setCheckingId] = React.useState<string | null>(null);
  // #710: the platform-hosted path, and the retry route registration points at when provisioning
  // failed there.
  const [localPart, setLocalPart] = React.useState("");
  const [pickerState, setPickerState] = React.useState<PickerState>({ kind: "idle" });
  const offer = useNeoMailboxOffer();

  const load = React.useCallback(() => {
    if (!tenantId) return;
    setError(null);
    cockpit.listTenantMailboxes(tenantId).then(
      (list) => setRows(Array.isArray(list) ? list : []),
      (e: any) => setError(e?.message || t("common.error")),
    );
  }, [tenantId, t]);

  React.useEffect(() => {
    // Reset per tenant: stale rows/forms/check results must not leak between businesses.
    setRows(rememberedMailboxes(tenantId));
    setMode(null);
    setCheck(null);
    setFormError(null);
    load();
  }, [load]);

  const openCreate = () => {
    setMode({ kind: "create" });
    setForm(emptyMailboxForm());
    setFormError(null);
  };

  const openChange = (row: TenantMailboxRow) => {
    setMode({ kind: "change", row });
    setLocalPart("");
    setPickerState({ kind: "idle" });
    setFormError(null);
  };

  const change = () => {
    if (!tenantId || pickedAddress(pickerState) === null) return;
    setBusy(true);
    setFormError(null);
    cockpit.changeNeoMailbox(tenantId, localPart.trim())
      .then((result) => {
        pushToast({
          tone: "success",
          title: t("neoMailbox.change.title"),
          msg: t("neoMailbox.changed", { address: result.emailAddress, previous: result.previous }),
        });
        setMode(null);
        load();
      })
      .catch((e: any) => setFormError(e?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  const openDomainChange = (row: TenantMailboxRow) => {
    setMode({ kind: "domainChange", row });
    setLocalPart("");
    setFormError(null);
  };

  const changeDomainAddress = () => {
    if (!tenantId || mode?.kind !== "domainChange" || !mode.row.mailDomainId || !localPart.trim()) return;
    setBusy(true);
    setFormError(null);
    cockpit.changeMailDomainAddress(tenantId, mode.row.mailDomainId, localPart.trim())
      .then((result) => {
        pushToast({
          tone: "success",
          title: t("mailDomainAddress.change.title"),
          msg: t("neoMailbox.changed", { address: result.emailAddress, previous: result.previous }),
        });
        setMode(null);
        load();
      })
      // The server's own sentence: on a failed provisioning it names the closed address and says
      // that re-enabling it restores the business, which is exactly what the owner needs next —
      // so the rows are read again, and the closed row shows its Enable action behind this form.
      .catch((e: any) => {
        setFormError(e?.message || t("common.error"));
        load();
      })
      .finally(() => setBusy(false));
  };

  const openNeo = () => {
    setMode({ kind: "neo" });
    setLocalPart("");
    setPickerState({ kind: "idle" });
    setFormError(null);
  };

  const provision = () => {
    const address = pickedAddress(pickerState);
    if (!tenantId || !address) return;
    setBusy(true);
    setFormError(null);
    cockpit.provisionNeoMailbox(tenantId, localPart.trim())
      .then((result) => {
        pushToast({
          tone: "success",
          title: t("mailbox.created"),
          msg: t("neoMailbox.provisioned", { address: result.emailAddress }),
        });
        setMode(null);
        load();
      })
      .catch((e: any) => setFormError(e?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  const openEdit = (row: TenantMailboxRow) => {
    setMode({ kind: "edit", row });
    setForm(mailboxFormFromRow(row));
    setFormError(null);
  };

  const save = () => {
    // The platform-hosted path has its own submit (`provision`); this one is the bring-your-own form.
    if (!tenantId || !mode || mode.kind === "neo" || mode.kind === "change" || mode.kind === "domainChange") return;
    setBusy(true);
    setFormError(null);
    const op =
      mode.kind === "create"
        ? cockpit.createTenantMailbox(tenantId, buildCreatePayload(form))
        : cockpit.updateTenantMailbox(tenantId, mode.row.id, buildUpdatePayload(form, mode.row));
    op.then(() => {
      pushToast({ tone: "success", title: t(mode.kind === "create" ? "mailbox.created" : "mailbox.updated") });
      setMode(null);
      load();
    })
      .catch((e: any) => setFormError(e?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  const toggleEnabled = (row: TenantMailboxRow) => {
    if (!tenantId) return;
    cockpit.setTenantMailboxEnabled(tenantId, row.id, !row.enabled, row.version)
      .then(() => {
        pushToast({ tone: "success", title: t(row.enabled ? "mailbox.disabledToast" : "mailbox.enabledToast") });
        load();
      })
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }));
  };

  const runCheck = (row: TenantMailboxRow) => {
    if (!tenantId) return;
    setCheckingId(row.id);
    setCheck(null);
    cockpit.checkTenantMailbox(tenantId, row.id)
      .then((result) => setCheck({ email: row.imapEmail, result }))
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }))
      .finally(() => setCheckingId(null));
  };

  const errors =
    mode && mode.kind !== "neo" && mode.kind !== "change" && mode.kind !== "domainChange"
      ? mailboxFormErrors(form, mode.kind)
      : [];
  const field = (key: keyof MailboxForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const flag = (key: keyof MailboxForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    // #2370: read it now. React clears currentTarget once this handler returns, and the updater
    // can run later, during render, where the throw blanked the whole console.
    const checked = e.currentTarget.checked;
    setForm((f) => ({ ...f, [key]: checked }));
  };

  return (
      <div className="stack-sm management-editor" style={{ paddingBottom: "var(--space-4)", minWidth: 0 }} data-testid="mailboxes-dialog">
        {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
        {!rows && !error && <p className="subtle">{t("common.loading")}</p>}

        {rows && !mode && (
          <>
            {rows.map((row) => (
              <div key={row.id} className="stack-sm" data-testid={`mailbox-row-${row.imapEmail}`}
                style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "var(--space-3)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
                  <b className="neo-mono">{row.imapEmail}</b>
                  <span className="quiet-flag neo-mono">{row.imapFolder}</span>
                  <StatusPill
                    size="sm"
                    variant="soft"
                    tone={row.enabled ? "success" : "neutral"}
                    label={t(row.enabled ? "mailbox.enabled" : row.retiredAt ? "mailbox.retired" : "mailbox.disabled")}
                  />
                </div>
                <div className="subtle" style={{ fontSize: "var(--text-sm)" }}>
                  {row.imapHost}:{row.imapPort}
                  {" · "}
                  {t("mailbox.password")}: <span className="neo-mono">{row.imapPasswordSet ? MASK : t("common.notSet")}</span>
                  {row.smtpHost && (
                    <>
                      {" · SMTP "}
                      {row.smtpHost}:{row.smtpPort}
                      {" · "}
                      {t("mailbox.password")}: <span className="neo-mono">{row.smtpPasswordSet ? MASK : t("common.notSet")}</span>
                    </>
                  )}
                </div>
                <div className="management-actions mailbox-actions">
                  <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={14} />} onClick={() => openEdit(row)}>{t("mailbox.editAction")}</Button>
                  {/* #779: offered only for the address the platform actually runs, and only while it is
                      the live one — there is nothing to correct about a closed address. Also
                      needs an installation that still hosts mailboxes: without one there is
                      nowhere to create the replacement, so the form would dead-end. */}
                  {row.neoHosted && row.enabled && !row.mailDomainId && offer?.offered && (
                    <>
                      <button className="linkbtn" onClick={() => openChange(row)}>
                        <Icon name="pencil" size={14} />
                        {t("neoMailbox.change")}
                      </button>
                    </>
                  )}
                  {/* #1447: the address of a business's own connected domain. Only on the live row: a
                      closed one is either history or the address a failed change left to re-enable. */}
                  {row.mailDomainId && row.enabled && (
                    <button className="linkbtn" onClick={() => openDomainChange(row)} data-testid="mail-domain-address-change">
                      <Icon name="pencil" size={14} />
                      {t("mailDomainAddress.change")}
                    </button>
                  )}
                  {/* #1475: a retired address has no account left to switch on; the server refuses it too. */}
                  {!row.enabled && !row.retiredAt && <Button variant="secondary" size="sm" onClick={() => toggleEnabled(row)}>
                    {t("mailbox.enable")}
                  </Button>}
                  <Button variant="secondary" size="sm" loading={checkingId === row.id} disabled={checkingId === row.id} onClick={() => runCheck(row)}>
                    {t("mailbox.check")}
                  </Button>
                </div>
                {row.enabled && (
                  <div className="management-actions reqventos-mailbox-danger">
                    <Button variant="danger-quiet" size="sm" iconLeft={<Icon name="power" size={16} />} onClick={() => toggleEnabled(row)}>
                      {t("mailbox.disable")}
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {!rows.length && <p className="subtle">{t("mailbox.none")}</p>}

            {check && (
              <Banner
                tone={check.result.imap.ok && check.result.smtp?.ok !== false ? "success" : "warning"}
                title={t("mailbox.checkTitle")}
                data-testid="mailbox-check-result"
              >
                <p style={{ margin: 0 }}>
                  IMAP ({check.email}):{" "}
                  {check.result.imap.ok
                    ? `${t("mailbox.checkOk")} · ${t("mailbox.checkMessages", { count: check.result.imap.messageCount ?? 0 })}`
                    : `${t("mailbox.checkFail")}${check.result.imap.error ? ` — ${check.result.imap.error}` : ""}`}
                </p>
                <p style={{ margin: 0 }}>
                  SMTP:{" "}
                  {check.result.smtp === null
                    ? t("mailbox.checkNoSmtp")
                    : check.result.smtp.ok
                      ? t("mailbox.checkOk")
                      : `${t("mailbox.checkFail")}${check.result.smtp.error ? ` — ${check.result.smtp.error}` : ""}`}
                </p>
              </Banner>
            )}

            <div className="management-actions">
              {/* #710: offered first, because it is the path that needs no credentials. Hidden
                  entirely where the installation hosts no mailboxes. */}
              {offer?.offered && (
                <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={openNeo}>
                  {t("neoMailbox.addHosted")}
                </Button>
              )}
              <Button
                variant={offer?.offered ? "secondary" : "primary"}
                size="sm"
                iconLeft={<Icon name="plus" size={14} />}
                onClick={openCreate}
              >
                {offer?.offered ? t("neoMailbox.addOwn") : t("mailbox.add")}
              </Button>
            </div>
          </>
        )}

        {rows && mode?.kind === "change" && (
          <div className="stack-sm" data-testid="neo-mailbox-change">
            <div className="neo-eyebrow">{t("neoMailbox.change.title")}</div>
            {formError && <Banner tone="danger" title={t("common.error")}>{formError}</Banner>}
            {/* Said before the choice, not after it: this closes an address customers already
                have, and no later screen can undo that. */}
            <Banner tone="warning" title={t("neoMailbox.change.title")}>
              {t("neoMailbox.change.warning", { address: mode.row.imapEmail })}
            </Banner>
            <NeoMailboxPicker
              localPart={localPart}
              onLocalPartChange={setLocalPart}
              onStateChange={setPickerState}
              domain={offer?.domain ?? null}
              inputId="mb-neo-change-local-part"
            />
            <div className="management-actions">
              <Button variant="ghost" disabled={busy} onClick={() => setMode(null)}>{t("common.cancel")}</Button>
              <Button loading={busy}
                variant="destructive"
                disabled={busy || pickedAddress(pickerState) === null}
                onClick={change}
              >
                {t("neoMailbox.change.confirm")}
              </Button>
            </div>
          </div>
        )}

        {rows && mode?.kind === "domainChange" && (
          <div className="stack-sm" data-testid="mail-domain-address-change-form">
            <div className="neo-eyebrow">{t("mailDomainAddress.change.title")}</div>
            {formError && <Banner tone="danger" title={t("common.error")}>{formError}</Banner>}
            {/* Said before the choice: this closes an address the business's customers already use. */}
            <Banner tone="warning" title={t("mailDomainAddress.change.title")}>
              {t("mailDomainAddress.change.warning", { address: mode.row.imapEmail })}
            </Banner>
            <FormField label={t("mailDomain.localPart")} htmlFor="mb-domain-change-local-part">
              <Input
                id="mb-domain-change-local-part"
                value={localPart}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalPart(e.target.value)}
                data-testid="mail-domain-address-change-input"
              />
            </FormField>
            {localPart.trim() && (
              <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                {t("mailDomainAddress.change.preview", {
                  address: `${localPart.trim().toLowerCase()}@${mode.row.imapEmail.slice(mode.row.imapEmail.lastIndexOf("@") + 1)}`,
                })}
              </p>
            )}
            <div className="management-actions">
              <Button variant="ghost" disabled={busy} onClick={() => setMode(null)}>{t("common.cancel")}</Button>
              <Button loading={busy}
                variant="destructive"
                disabled={busy || !localPart.trim()}
                onClick={changeDomainAddress}
                data-testid="mail-domain-address-change-confirm"
              >
                {t("neoMailbox.change.confirm")}
              </Button>
            </div>
          </div>
        )}

        {rows && mode?.kind === "neo" && (
          <div className="stack-sm" data-testid="neo-mailbox-form">
            <div className="neo-eyebrow">{t("neoMailbox.addHosted")}</div>
            {formError && <Banner tone="danger" title={t("common.error")}>{formError}</Banner>}
            <NeoMailboxPicker
              localPart={localPart}
              onLocalPartChange={setLocalPart}
              onStateChange={setPickerState}
              domain={offer?.domain ?? null}
              inputId="mb-neo-local-part"
            />
            <div className="management-actions">
              <Button variant="ghost" disabled={busy} onClick={() => setMode(null)}>{t("common.cancel")}</Button>
              <Button loading={busy}
                variant="primary"
                disabled={busy || pickedAddress(pickerState) === null}
                onClick={provision}
              >
                {t("mailbox.save")}
              </Button>
            </div>
          </div>
        )}

        {rows && mode && mode.kind !== "neo" && mode.kind !== "change" && mode.kind !== "domainChange" && (
          <>
            <div className="neo-eyebrow">{t(mode.kind === "create" ? "mailbox.add" : "mailbox.edit")}</div>
            {formError && <Banner tone="danger" title={t("common.error")}>{formError}</Banner>}
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>{t("mailbox.credsNote")}</p>

            <div className="neo-eyebrow" style={{ marginTop: "var(--space-2)" }}>IMAP</div>
            <FormField label={t("mailbox.host")} htmlFor="mb-imap-host">
              <Input id="mb-imap-host" value={form.imapHost} onChange={field("imapHost")} placeholder="imap.example.com" />
            </FormField>
            <FormField label={t("mailbox.port")} htmlFor="mb-imap-port">
              <Input id="mb-imap-port" value={form.imapPort} onChange={field("imapPort")} />
            </FormField>
            <FormField label={t("mailbox.email")} htmlFor="mb-imap-email">
              <Input id="mb-imap-email" value={form.imapEmail} onChange={field("imapEmail")} placeholder="inbox@example.com" />
            </FormField>
            <FormField label={t("mailbox.folder")} htmlFor="mb-imap-folder">
              <Input id="mb-imap-folder" value={form.imapFolder} onChange={field("imapFolder")} />
            </FormField>
            <FormField label={t("mailbox.imapPassword")} htmlFor="mb-imap-password">
              <Input
                id="mb-imap-password"
                type="password"
                autoComplete="new-password"
                value={form.imapPassword}
                onChange={field("imapPassword")}
                placeholder={mode.kind === "edit" && mode.row.imapPasswordSet ? MASK : undefined}
              />
            </FormField>
            {mode.kind === "edit" && (
              <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>{t("mailbox.passwordKeepHint")}</p>
            )}
            <Switch
              checked={form.imapTlsAllowHostnameMismatch}
              onChange={flag("imapTlsAllowHostnameMismatch")}
              label={t("mailbox.tlsMismatch")}
            />
            {form.imapTlsAllowHostnameMismatch && (
              <FormField label={t("mailbox.tlsServername")} htmlFor="mb-imap-servername">
                <Input id="mb-imap-servername" value={form.imapTlsExpectedServername} onChange={field("imapTlsExpectedServername")} placeholder="sslin.de" />
              </FormField>
            )}

            <div className="neo-eyebrow" style={{ marginTop: "var(--space-2)" }}>SMTP</div>
            <FormField label={t("mailbox.host")} htmlFor="mb-smtp-host">
              <Input id="mb-smtp-host" value={form.smtpHost} onChange={field("smtpHost")} placeholder="smtp.example.com" />
            </FormField>
            <FormField label={t("mailbox.port")} htmlFor="mb-smtp-port">
              <Input id="mb-smtp-port" value={form.smtpPort} onChange={field("smtpPort")} />
            </FormField>
            <FormField label={t("mailbox.smtpUser")} htmlFor="mb-smtp-user">
              <Input id="mb-smtp-user" value={form.smtpUser} onChange={field("smtpUser")} />
            </FormField>
            <FormField label={t("mailbox.smtpPassword")} htmlFor="mb-smtp-password">
              <Input
                id="mb-smtp-password"
                type="password"
                autoComplete="new-password"
                value={form.smtpPassword}
                onChange={field("smtpPassword")}
                placeholder={mode.kind === "edit" && mode.row.smtpPasswordSet ? MASK : undefined}
              />
            </FormField>
            <FormField label={t("mailbox.smtpFrom")} htmlFor="mb-smtp-from">
              <Input id="mb-smtp-from" value={form.smtpFrom} onChange={field("smtpFrom")} />
            </FormField>
            <FormField label={t("mailbox.smtpFromName")} htmlFor="mb-smtp-from-name">
              <Input id="mb-smtp-from-name" value={form.smtpFromName} onChange={field("smtpFromName")} />
            </FormField>
            <Switch checked={form.smtpSecure} onChange={flag("smtpSecure")} label={t("mailbox.smtpSecure")} />
            <Switch
              checked={form.smtpTlsAllowHostnameMismatch}
              onChange={flag("smtpTlsAllowHostnameMismatch")}
              label={t("mailbox.tlsMismatch")}
            />

            {errors.length > 0 && (
              <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                {errors.map((k) => t(k)).join(" · ")}
              </p>
            )}
            <div className="management-actions">
              <Button variant="ghost" disabled={busy} onClick={() => setMode(null)}>{t("common.cancel")}</Button>
              <Button loading={busy} variant="primary" disabled={busy || errors.length > 0} onClick={save}>
                {t("mailbox.save")}
              </Button>
            </div>
          </>
        )}
      </div>
  );
}
